import { useEffect, useState } from 'react';
import { Intent } from '@blueprintjs/core';

import { NotificationToaster } from 'components/notifications';
import { AccountService } from 'services';

const accountsErrorMessage = "Could not retrieve accounts, please try again.";
const restrictedAccountTooltip = "You do not have permission to run the rules engine for this account";
const checkingAccountsTooltip = "Checking your account permissions";
const accountsUncheckedTooltip = "Your account permissions could not be checked";
const noAccountTooltip = "This driver has no current account, so the harness cannot run for them";

const defaultArray = [];
const defaultFunction = () => null;

// The same per-account restriction Global Search applies. A caller that keeps the ids across remounts
// passes them back as knownIds, which skips the lookup.
export function useRestrictedAccountIds(
    {
        enabled = true,
        knownIds = null,
        onLoaded = defaultFunction
    } = {}
) {
    const [loadedIds, setLoadedIds] = useState(null);
    const [lookupFailed, setLookupFailed] = useState(false);

    const restrictedAccountIds = knownIds ?? loadedIds;

    useEffect(loadRestrictedAccounts, [enabled]);

    function loadRestrictedAccounts() {
        if (!enabled || restrictedAccountIds != null) {
            return;
        }

        AccountService.getAllAccounts().then((response) => {
            const accounts = response?.data ?? defaultArray;
            const ids = accounts.filter((account) => account.restricted === true).map((account) => account.id);

            setLoadedIds(ids);
            onLoaded(ids);
        }, () => {
            setLookupFailed(true);
            NotificationToaster.show(Intent.DANGER, accountsErrorMessage);
        });
    }

    // A driver with no account cannot be run or judged, so that comes first; the endpoints do not check the
    // restriction, so every account counts as restricted until the list is known.
    function blockedReason(accountId) {
        if (accountId == null) {
            return noAccountTooltip;
        }

        if (restrictedAccountIds == null) {
            if (lookupFailed) {
                return accountsUncheckedTooltip;
            }

            return checkingAccountsTooltip;
        }

        if (restrictedAccountIds.includes(accountId)) {
            return restrictedAccountTooltip;
        }

        return null;
    }

    return {
        restrictedAccountIds,
        lookupFailed,
        blockedReason
    };
}
